import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import i18n from '../../i18n/initI18n';
import { navigateReplaceUrl } from '../../reducers/navigationActions';
import { buildUrl } from '../../../common/utils/urlUtil';
import { getOptionalLocaleForCMS } from '../../../common/cms/localeValidationUtils';
import { APP_QUERY_PARAMS } from '../../../common/utils/urlConstants';
import { getReleasedAuthLocaleOptions, getAuthLocale, getAuthLocaleQuery } from './authLocaleService';
import { AUTH_LOCALE_ROUTE_PATHS } from './authLocaleConstants';
import { getSavedAuthLocale, saveAuthLocale } from './authLocaleStorage';
import { type LocaleOption } from '../../user/account/accountModal/localisation/localizationConstants';
import globalLogger from '../../logger';

const logger = globalLogger.createChannel('auth.locale');
const LOCALE_RESOLUTION_TIMEOUT_MS = 10000;

/**
 * Represents a location object containing pathname, query params, and hash.
 */
type AuthLocation = {
    pathname?: string;
    query?: Record<string, unknown>;
    hash?: string;
};

/**
 * Options for configuring the auth locale manager.
 */
type UseAuthLocaleManagerOptions = {
    /** The current location object containing pathname, query, and hash */
    location?: AuthLocation;
    /** Override locale from URL (takes precedence over location.query) */
    urlLocale?: string | null;
};

/**
 * Resolves the active auth locale using a priority fallback strategy:
 * 1. URL query parameter (if present on entry)
 * 2. localStorage
 * 3. Device/browser language
 * 4. Default locale
 *
 * Keeps i18n and localStorage synchronized. The URL query parameter is only
 * preserved when it was present on initial entry to maintain deep-link behavior.
 *
 * @param options - Configuration containing location and optional urlLocale override
 * @returns Object with locale options, selected locale, and selection handler
 */
const useAuthLocaleManager = ({ location, urlLocale }: UseAuthLocaleManagerOptions = {}) => {
    const dispatch = useDispatch();
    const [localeOptions, setLocaleOptions] = useState<LocaleOption[]>([]);
    const [selectedLocale, setSelectedLocale] = useState<string | null>(() => getSavedAuthLocale());
    const [hasResolvedLocale, setHasResolvedLocale] = useState(false);

    // Determine if we're on an auth route that supports locale selection
    const isAuthLocaleRoute = !!(location?.pathname && AUTH_LOCALE_ROUTE_PATHS.has(location.pathname));

    // Capture the locale from URL query params on first entry (for deep-link support)
    const entryQueryLocale = useRef(isAuthLocaleRoute ? getAuthLocaleQuery(location?.query) : null);

    // Only sync URL query param if it was present when the component first mounted
    const shouldSyncQuery = isAuthLocaleRoute && !!entryQueryLocale.current;

    /**
     * Applies the given locale by updating state, localStorage, and i18n.
     */
    const applyLocale = useCallback(async (locale: string, persist = false): Promise<void> => {
        setSelectedLocale(locale);

        if (persist) {
            saveAuthLocale(locale);
        }

        if (i18n.language === locale) return;

        await i18n.changeLanguage(locale);
    }, []);

    /**
     * Synchronizes the locale to the URL query parameter if it was present on entry.
     * This maintains deep-link behavior while avoiding unnecessary URL updates.
     */
    const syncQueryLocale = useCallback(
        (locale: string) => {
            if (!shouldSyncQuery || !location?.pathname) return;
            if (getAuthLocaleQuery(location.query) === locale) return;

            // Build new URL with updated locale query parameter
            const url = buildUrl(location.pathname, {
                ...location.query,
                [APP_QUERY_PARAMS.APP_LOCALE]: locale,
            });
            const nextUrl = location.hash ? `${url}${location.hash}` : url;

            dispatch(navigateReplaceUrl(nextUrl));
        },
        [shouldSyncQuery, location, dispatch],
    );

    /**
     * Applies the locale and then synchronizes the URL query parameter if needed.
     */
    const applyLocaleAndSyncQuery = useCallback(
        async (locale: string, persist = false): Promise<void> => {
            await applyLocale(locale, persist);
            syncQueryLocale(locale);
        },
        [applyLocale, syncQueryLocale],
    );

    useEffect(() => {
        let active = true;
        const resolveTimer = window.setTimeout(() => {
            if (!active) return;

            // Avoid leaving auth screens blank if locale resolution hangs.
            logger.warn('Auth locale resolution timed out; continuing with current locale state');
            setHasResolvedLocale(true);
        }, LOCALE_RESOLUTION_TIMEOUT_MS);

        const initialize = async () => {
            try {
                // Fetch available locale options and resolve the appropriate locale in parallel
                const [options, locale] = await Promise.all([
                    getReleasedAuthLocaleOptions(),
                    getAuthLocale({
                        urlLocale: urlLocale || getAuthLocaleQuery(location?.query),
                        savedLocale: getSavedAuthLocale(),
                    }),
                ]);
                if (!active) return;

                setLocaleOptions(options);
                await applyLocaleAndSyncQuery(locale);
            } finally {
                window.clearTimeout(resolveTimer);

                if (active) {
                    setHasResolvedLocale(true);
                }
            }
        };

        initialize();

        return () => {
            active = false;
            window.clearTimeout(resolveTimer);
        };
    }, [location?.pathname, location?.query?.[APP_QUERY_PARAMS.APP_LOCALE], applyLocaleAndSyncQuery, urlLocale]);

    /**
     * Handles user selection of a new locale from the UI.
     * Validates the selection before applying it.
     */
    const onSelectLocale = useCallback(
        (value: string | null) => {
            const locale = getOptionalLocaleForCMS(value);
            if (!locale || !localeOptions.some(({ id }) => id === locale)) return;

            void applyLocaleAndSyncQuery(locale, true);
        },
        [applyLocaleAndSyncQuery, localeOptions],
    );

    return { localeOptions, selectedLocale, onSelectLocale, hasResolvedLocale };
};

export default useAuthLocaleManager;
